import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import autoLogin from "./autoLogin";
import { setUser } from "@redux/actions/authActions";
import { toast } from "react-toastify";
import { getRemoteConfigValue } from "../../redux/actions/remoteConfigActions";
import { doc, getDoc } from "firebase/firestore";
import { firestore, setAnalyticsUserId } from "../../redux/firebase";
import { getLastVisitedPage, getDefaultAuthenticatedPage, prendiDopoAccesso } from "../../utils/lastPageTracker";
import { clearUserCache, clearSessionCache } from "../../utils/userCacheUtils";
import { initPixelWithUserData } from "@utils/facebookPixel";
import { hasEntitlement, rottaAccountDaCheckout } from '@pages/paymentSuccess/entitlement';

const useAuthentication = () => {
  const [loader, setLoader] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // check if user signed in before
  const checkUserSign = useCallback(() => {
    return onAuthStateChanged(getAuth(), async user => {
      if (!user) {
        setLoader(false);
        return;
      }

      // DataFast identify — attribuzione sorgente del revenue.
      // Lega QUESTO visitatore al cliente tramite email, per OGNI utente
      // autenticato (signup, login, sessioni di ritorno), non solo ai signup nuovi.
      // Perché qui e non solo al signup: l'estensione `firestore-stripe-payments`,
      // in mode:subscription, scrive i metadata su `subscription_data.metadata`
      // (sulla Subscription, NON sulla Checkout Session) → DataFast non li legge.
      // Quindi l'unico canale che attribuisce abbonamenti e RINNOVI alla sorgente
      // è il match per email (customer Stripe ↔ visitatore identificato).
      // `window.datafast` è uno stub con queue (vedi index.html): se lo script non
      // è ancora caricato la chiamata viene accodata, non persa.
      if (user.email) {
        const dfIdentifyKey = `df_identified_${user.email}`;
        if (!sessionStorage.getItem(dfIdentifyKey)) {
          sessionStorage.setItem(dfIdentifyKey, '1');
          window?.datafast?.('identify', { user_id: user.email });
        }
      }

      // NOTE: il render-gate (`loader`) NON viene aperto qui: per gli utenti
      // loggati resta attivo finché `setUser` non è in Redux (vedi sotto), così
      // ProtectedRoute non vede `user === null` e non rimbalza a "/" un utente
      // autenticato che ricarica /home/*.

      // NOTE: Signup tracking has been moved to the actual signup functions
      // (signUpAction, useSignInGoogle, useSignInApple) to avoid duplicate events
      // and ensure accurate tracking at the point of account creation

      // CRITICAL: Controlla provider per OAuth users (Google/Apple hanno email auto-verificata)
      const isOAuthProvider = user.providerData?.some(
        provider => provider.providerId === 'google.com' || provider.providerId === 'apple.com'
      );

      const currentPath = window.location.pathname;

      // PERF (LCP): la lettura del profilo per autoLogin e il getDoc di stato
      // (verifica email / setup password) sono Firestore read INDIPENDENTI.
      // Prima erano serializzate (getDoc → poi autoLogin), raddoppiando la
      // finestra di rete bloccante prima che il gate si aprisse. Le lanciamo in
      // parallelo: la finestra bloccante = max(read), non somma.
      // Wrappiamo in un risultato "settled" così la promise non genera mai un
      // unhandled rejection nei rami che fanno return anticipato (verifica
      // email / payment-completed) senza attenderla.
      const userInfoPromise = autoLogin(user).then(
        (value) => ({ ok: true, value }),
        (error) => ({ ok: false, error })
      );

      // 🆕 NUOVO: Controlla lo stato dell'utente dal documento Firestore
      let needsPasswordSetup = false;
      let hasPaidSubscription = false;
      // Accesso Premium VERO (plan/trial/abbonamento/genitore/regalo). Serve
      // perché `hasPaidSubscription` qui sotto è vero per chiunque abbia scelto un
      // piano nell'onboarding: `source: 'welcome_flow_checkout'` viene scritto
      // PRIMA del redirect a Stripe, pagamento o no (STU-315).
      let entitledNow = false;
      try {
        const userDocRef = doc(firestore, 'students', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          needsPasswordSetup = userData.needsPasswordSetup === true;
          // Chi può stare nell'app senza aver verificato l'email: password già
          // impostata, account genitore, regalo riscattato, Premium attivo.
          // `source: 'welcome_flow_checkout'` NON sta piu' qui: e' scritto PRIMA di
          // Stripe per chiunque scelga un piano, quindi valeva come prova di
          // pagamento anche per chi usciva dal checkout senza pagare (STU-322).
          hasPaidSubscription = userData.passwordSetAt != null ||
                                userData.is_parent_account === true ||
                                userData.subscription_type === 'gift' ||
                                userData.plan === 'Premium';
          entitledNow = hasEntitlement(userData);
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      }

      // Se l'utente non ha verificato l'email E non è OAuth E NON deve impostare password
      // E NON ha pagato → vai a verifica email
      // (utenti che hanno pagato possono accedere senza verifica email)
      if (!user.emailVerified && !isOAuthProvider && !needsPasswordSetup && !hasPaidSubscription) {
        setLoader(false);
        return navigate("/controlla-verifica-email", { replace: true });
      }
      
      // Account nato nel checkout dell'onboarding (password temporanea, creato prima
      // che ci fosse una carta). Con l'accesso vero va sul form della password; senza,
      // non entra nell'app: resta dov'è e può ripagare o scegliere il gratuito
      // dall'offerta. Prima cadeva nella coda di questa funzione, che lo portava su
      // /home da piano gratuito, con l'email non verificata (STU-322).
      const rottaCheckout = rottaAccountDaCheckout({
        needsPasswordSetup,
        entitled: entitledNow,
        currentPath,
      });
      if (rottaCheckout === 'password') {
        setLoader(false);
        return navigate("/payment-completed", { replace: true });
      }
      if (rottaCheckout === 'offerta') {
        setLoader(false);
        return navigate("/welcome?screen=offer", { replace: true });
      }
      if (rottaCheckout === 'ferma') {
        setLoader(false);
        return;
      }

      // Controlla se è un login Google o Apple in corso
      const isGoogleAuth = user.providerData[0]?.providerId === "google.com";
      const isAppleAuth = user.providerData[0]?.providerId === "apple.com";
      const isOAuthAuth = isGoogleAuth || isAppleAuth;
      const isAuthPage = window.location.pathname === "/signIn" || window.location.pathname === "/signUp";

      // Se è OAuth auth e siamo su pagina auth, non fare nulla (getRedirectResult gestirà)
      if (isOAuthAuth && isAuthPage) {
        setLoader(false);
        return;
      }

      // Per tutti i casi, procedi con l'autoLogin per setup utente
      // (la promise è già stata avviata in parallelo al getDoc di stato sopra)
      try {
        const settled = await userInfoPromise;
        if (!settled.ok) {
          // Rilancia per far gestire l'errore al catch dedicato sotto
          throw settled.error;
        }
        const userInfo = settled.value;
        dispatch(setUser(userInfo));

        // Imposta User-ID in Firebase Analytics per tracking cross-device
        if (user?.uid) {
          setAnalyticsUserId(user.uid);
        }

        // Inizializza Meta Pixel Advanced Matching con dati utente
        if (userInfo) {
          initPixelWithUserData({ ...userInfo, uid: user.uid });
        }

        setLoader(false);
        
        // Se siamo sulla pagina di login/signup, NON fare redirect
        // lascia che useSignIn gestisca la navigazione
        if (isAuthPage) {
          return;
        }
        
        // Controlla se siamo in una pagina chat senza state (dovremmo andare a ripeti)
        // const currentPath = window.location.pathname; // Già definita sopra
        const isInvalidChatPage = currentPath.startsWith('/chat/');
        
        if (isInvalidChatPage) {
          // Se siamo in una pagina chat senza state valido, vai a ripeti
          navigate('/home/ripeti', { replace: true });
          return;
        }

        // Se l'utente è già su una pagina autenticata, non sovrascrivere la navigazione
        if (currentPath.startsWith('/home')) {
          return;
        }

        // Tenta di recuperare l'ultima pagina visitata per utenti autenticati
        const lastPage = getLastVisitedPage(true);
        const targetPage = prendiDopoAccesso() || lastPage || getDefaultAuthenticatedPage();

        navigate(targetPage, { replace: true });
      } catch (error) {
        console.error("Auto-login failed:", error);
        setLoader(false);

        // Distingui errori di rete/temporanei da errori di autenticazione reali
        const isNetworkError = error?.code === 'unavailable' ||
          error?.code === 'resource-exhausted' ||
          error?.message?.includes('network') ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('offline');

        if (isNetworkError) {
          // Errore di rete: NON fare logout, l'utente potrebbe avere dati dalla cache
          console.warn("Errore di rete durante autoLogin, mantengo la sessione");
          toast.warning("Connessione instabile. Alcuni dati potrebbero non essere aggiornati.");
          return;
        }

        // Errore di autenticazione reale: esegui il logout
        try {
          const userId = JSON.parse(localStorage.getItem("userId") || 'null');
          if (userId) {
            clearUserCache(userId);
          }

          await getAuth().signOut();
          localStorage.removeItem("userId");
          clearSessionCache();

          toast.error("Sessione scaduta. Effettua nuovamente il login.");
          navigate('/signIn', { replace: true });
        } catch (signOutError) {
          console.error("Errore durante il logout automatico:", signOutError);
          toast.error("Si è verificato un errore. Ricarica la pagina.");
        }
      }
    });
  }, [dispatch, navigate]);

  useEffect(() => {
    const unsubscribe = checkUserSign();
    return () => {
      unsubscribe();
    };
  }, [checkUserSign]);

  return { loader };
};

export default useAuthentication;
